import React, { useState, useEffect, useMemo } from 'react';
import useDimensions from 'react-use-dimensions';

import Localization from './Localization';
import DataLoader from './DataLoader';
import Modal from './Modal';
import Introduction from './Introduction';
import Header from './Header';
import Navigation from './Navigation';
import Overview from './Overview';
import Comparison from './Comparison';

import './App.scss';

let prevGeo = 'countries';

function App() {
  const [language, setLanguage] = useState(Localization.getLanguage());
  const [data, setData] = useState({ investments: [], years: [], countries: [], regions: [] });
  useEffect(() => {
    DataLoader.load().then(_data => {
      setYearSelection(_data.years);
      setData(_data);
    });
  }, []);
  const countries = useMemo(() => ['ALL'].concat(data.countries), [data.countries]);
  const regions = useMemo(() => ['ALL'].concat(data.regions.map(r => r.key)), [data.regions]);

  const [headerRef, headerDimensions] = useDimensions();
  const [showIntro, setShowIntro] = useState(true);
  const [view, setView] = useState('overview');
  const [metric, setMetric] = useState('capacity');
  const [yearSelection, setYearSelection] = useState([]);
  const [geo, setGeo] = useState({ type: prevGeo, selection: '', noTransform: false });
  const [lockedProject, setLockedProject] = useState(null);
  useEffect(() => {
    setLockedProject(null)
  }, [metric])
  const updateLanguage = (lang) => {
    Localization.setLanguage(lang);
    const language = Localization.getLanguage();
    window.history.pushState(lang, lang, `?lang=${language}${document.location.hash}`);
    setLanguage(language);
  }

  const updateGeo = useMemo(() => function (key, value) {
    setGeo(geo => {
      const nextGeo = Object.assign({}, geo);
      nextGeo[key] = value;
      nextGeo.noTransform = false;
      if (key === 'selection' && value === '' && geo.type === 'states') {
        nextGeo.type = prevGeo;
      }
      if (key === 'type' && value !== geo.type) {
        nextGeo.type = geo.type === 'states' && value !== 'states' && value !== prevGeo ? 'states' : value;
        if (value !== 'states' && value !== prevGeo && nextGeo.selection !== '') {
          const getRegion = value === 'regions';
          if (lockedProject) {
            nextGeo.selection = getRegion ? lockedProject.region : lockedProject.country;
          } else {
            nextGeo.selection = getRegion
              ? data.regions.find(r => r.countries.map(c => c.name).includes(nextGeo.selection)).key || ''
              : '';
            nextGeo.noTransform = nextGeo.selection === '';
            nextGeo.type = nextGeo.noTransform
              ? getRegion ? 'regions' : 'countries'
              : nextGeo.type;
          }
        }
        prevGeo = value === 'states' ? prevGeo : value;
      }
      if (lockedProject && (nextGeo.type !== 'states' || nextGeo.selection !== lockedProject.country)) {
        setLockedProject(null);
      }
      return nextGeo;
    });
  }, [lockedProject, data.regions]);

  useEffect(() => {
    if (lockedProject) {
      const selection = (geo.type === 'regions' || prevGeo === 'regions')
        ? lockedProject.region
        : lockedProject.country;
      if (geo.type !== 'states' || geo.selection !== selection) {
        setGeo(geo => {
          prevGeo = geo.type === 'states' ? prevGeo : geo.type;
          const type = 'states';
          return { type, selection };
        });
      }
    }
  }, [lockedProject, geo]);

  return (
    <div className="App">
      <Modal type='intro' show={showIntro} setShow={setShowIntro}>
        <Introduction setShow={setShowIntro} />
      </Modal>
      <div ref={headerRef}>
        <Header
          language={language}
          updateLanguage={updateLanguage}
          width={headerDimensions.width || 0}
        />
        <Navigation
          countries={countries}
          regions={regions}
          view={view}
          setView={setView}
          metric={metric}
          setMetric={setMetric}
          geoType={geo.type === 'states' ? prevGeo : geo.type}
          geoSelection={geo.selection}
          updateGeo={updateGeo}
          language={language}
        />
      </div>
      {view === 'overview' ? (
        <Overview
          data={data}
          metric={metric}
          prevGeo={prevGeo}
          geoType={geo.type}
          geoSelection={geo.selection}
          noTransform={geo.noTransform}
          updateGeo={updateGeo}
          yearSelection={yearSelection}
          setYearSelection={setYearSelection}
          lockedProject={lockedProject}
          setLockedProject={setLockedProject}
          headerHeight={headerDimensions.height || 0}
          language={language}
        />
      ) : (
        <Comparison
          data={data}
          language={language}
        />
      )}
    </div>
  );
}

export default App;
